/**
 * Helpers for branching persistent chats: sibling user turns share the same
 * parent_message_id; active_leaf_message_id on the chat selects which path is shown.
 */

export type ChatBranchMessage = {
  id: string;
  role: string;
  parent_message_id?: string | null;
  created_at: Date | string;
  metadata?: any;
};

function ts(m: ChatBranchMessage): number {
  const v = m.created_at;
  return v instanceof Date ? v.getTime() : new Date(v).getTime();
}

/** Path from root to leaf (inclusive), following parent_message_id links. */
export function linearPathToLeaf<T extends ChatBranchMessage>(
  messages: T[],
  leafId: string | null | undefined,
): T[] {
  if (!leafId) {
    if (messages.length === 0) return [];
    const sorted = [...messages].sort((a, b) => ts(a) - ts(b));
    const hasAnyParent = sorted.some((m) => m.parent_message_id);
    if (!hasAnyParent) return sorted;
    return [];
  }

  const byId = new Map<string, T>(messages.map((m) => [m.id, m]));
  const path: T[] = [];
  let cur: T | undefined = byId.get(leafId);
  const seen = new Set<string>();

  while (cur) {
    if (seen.has(cur.id)) break;
    seen.add(cur.id);
    path.push(cur);
    const pid = cur.parent_message_id;
    cur = pid ? byId.get(pid) : undefined;
  }

  path.reverse();
  return path;
}

export type LinearMessagesForDisplayOptions = {
  /**
   * While the agent is responding, keep this user turn on the visible branch path even when
   * `active_leaf_message_id` or parent links briefly point at an assistant-only slice.
   */
  inFlightUserMessageId?: string | null;
};

/** Prefer a leaf id that exists in the cached branch (e.g. after optimistic id → server id patch). */
export function resolveActiveLeafForDisplay<T extends ChatBranchMessage>(
  messages: T[],
  activeLeafId: string | null | undefined,
  inFlightUserMessageId?: string | null,
): string | null {
  if (!messages.length) return activeLeafId ?? null;
  const byId = new Map(messages.map((m) => [m.id, m]));
  if (activeLeafId && byId.has(activeLeafId)) return activeLeafId;
  if (inFlightUserMessageId && byId.has(inFlightUserMessageId)) return inFlightUserMessageId;
  return activeLeafId ?? null;
}

/**
 * When the active path omits the in-flight user row, insert it before the tail assistant for this turn.
 */
export function ensureInFlightUserOnDisplayPath<T extends ChatBranchMessage>(
  linear: T[],
  branchRawMessages: T[],
  inFlightUserMessageId: string,
): T[] {
  if (linear.some((m) => m.id === inFlightUserMessageId)) return linear;

  const userRow = branchRawMessages.find((m) => m.id === inFlightUserMessageId && m.role === "user");
  if (!userRow) return linear;

  const triggerIdx = linear.findIndex(
    (m) =>
      m.role === "assistant"
      && m.metadata?.triggerUserMessageId === inFlightUserMessageId,
  );
  if (triggerIdx >= 0) {
    return [...linear.slice(0, triggerIdx), userRow, ...linear.slice(triggerIdx)];
  }

  const last = linear[linear.length - 1];
  if (last?.role === "assistant" && last.parent_message_id === inFlightUserMessageId) {
    return [...linear.slice(0, -1), userRow, last];
  }

  return [...linear, userRow];
}

/**
 * Messages along the active branch for Story Chat UI, with safe fallbacks for stale leaves and
 * in-flight turns.
 */
export function linearMessagesForDisplay<T extends ChatBranchMessage>(
  messages: T[],
  activeLeafId: string | null | undefined,
  options?: LinearMessagesForDisplayOptions,
): T[] {
  const inFlightId = options?.inFlightUserMessageId ?? null;
  const leafId = resolveActiveLeafForDisplay(messages, activeLeafId ?? null, inFlightId);

  let linear = linearPathToLeaf(messages, leafId);
  if (linear.length === 0 && messages.length > 0) {
    linear = [...messages].sort((a, b) => ts(a) - ts(b));
  }

  if (inFlightId) {
    linear = ensureInFlightUserOnDisplayPath(linear, messages, inFlightId);
  }

  return linear;
}

/** User messages that share this parent (fork alternatives), oldest first. */
export function userBranchVariantsAtParent<T extends ChatBranchMessage>(messages: T[], parentId: string | null): T[] {
  return messages
    .filter((m) => m.role === "user" && (m.parent_message_id ?? null) === parentId)
    .sort((a, b) => ts(a) - ts(b));
}

/** True when any row in the chat tree lists `userMessageId` as its parent (any reply forked from that turn). */
export function userMessageHasChildInBranch<T extends ChatBranchMessage>(
  messages: T[],
  userMessageId: string,
): boolean {
  return messages.some((m) => m.parent_message_id === userMessageId);
}

/** Client-only optimistic user row ids before `chat_acknowledged` replaces them. */
export function isOptimisticClientUserMessageId(messageId: string | null | undefined): boolean {
  return typeof messageId === "string" && messageId.startsWith("optimistic-user-");
}

/**
 * Resend should create a sibling user fork only when the target turn already has a child
 * (e.g. a prior assistant reply). Stalled resends with no reply update the same user row.
 */
export function shouldBranchResendUserMessage<T extends ChatBranchMessage>(
  messages: T[],
  resendOfMessageId: string,
  intent?: 'retry' | 'edit',
): boolean {
  if (intent) return intent === 'edit';
  return userMessageHasChildInBranch(messages, resendOfMessageId);
}

/** Parent user message ids that already have a visible assistant reply. */
export function userMessageIdsWithVisibleAssistantReply<T extends ChatBranchMessage>(messages: T[]): Set<string> {
  const ids = new Set<string>();
  for (const row of messages) {
    if (row?.role !== "assistant") continue;
    if (row?.metadata?.hiddenFromUI) continue;
    const parentId = row?.parent_message_id;
    if (typeof parentId !== "string" || !parentId) continue;
    ids.add(parentId);
  }
  return ids;
}

const childrenByParent = (messages: ChatBranchMessage[]): Map<string | null, string[]> => {
  const m = new Map<string | null, string[]>();
  for (const row of messages) {
    const p = row.parent_message_id ?? null;
    if (!m.has(p)) m.set(p, []);
    m.get(p)!.push(row.id);
  }
  return m;
};

/** Default number of user turns to load per chat history page (agentic Story Chat). */
export const DEFAULT_CHAT_TURN_PAGE_SIZE = 10;

/** Chats returned per project/series history list page (agentic Story Chat sidebar). */
export const CHAT_LIST_PAGE_SIZE = 40;

/** Matching messages loaded per chat for history-list search snippets. */
export const CHAT_SEARCH_SNIPPETS_PER_CHAT = 3;

/**
 * Slice a root-to-leaf path to the most recent `turnLimit` user turns, including
 * assistant/system rows between those user turns.
 */
export function slicePathToRecentTurns<T extends ChatBranchMessage>(
  path: T[],
  turnLimit: number,
): { slice: T[]; hasMoreOlder: boolean; oldestSliceMessageId: string | null } {
  if (turnLimit <= 0 || path.length === 0) {
    return { slice: path, hasMoreOlder: false, oldestSliceMessageId: path[0]?.id ?? null };
  }

  let userCount = 0;
  let startIndex = 0;
  for (let i = path.length - 1; i >= 0; i--) {
    if (path[i].role === "user") {
      userCount++;
      if (userCount >= turnLimit) {
        startIndex = i;
        break;
      }
    }
  }

  const slice = path.slice(startIndex);
  return {
    slice,
    hasMoreOlder: startIndex > 0,
    oldestSliceMessageId: slice[0]?.id ?? null,
  };
}

/** Deepest message id under rootId (max created_at among leaves). */
export function branchTipMessageId<T extends ChatBranchMessage>(messages: T[], rootId: string): string {
  const byId = new Map<string, T>(messages.map((r) => [r.id, r]));
  const children = childrenByParent(messages);
  const stack = [...(children.get(rootId) ?? [])];
  if (stack.length === 0) return rootId;

  const leaves: string[] = [];
  while (stack.length) {
    const id = stack.pop()!;
    const kids = children.get(id);
    if (!kids?.length) leaves.push(id);
    else stack.push(...kids);
  }

  return leaves.reduce((best, id) => (ts(byId.get(best)!) >= ts(byId.get(id)!) ? best : id), leaves[0]);
}
